import { ArrowRight, BarChart2, Home, MessageSquare, Star } from 'lucide-react'
import previewAskReinz from '@/assets/preview-askreinz.png'
import previewMarketInsights from '@/assets/preview-market-insights.png'
import previewMemberBenefits from '@/assets/preview-member-benefits.png'
import previewPropertyTools from '@/assets/preview-property-tools.png'
import reinzLogo from '@/assets/reinz-logo.png'
import reinzLogoFooter from '@/assets/reinz-logo-footer.png'

interface LandingScreenProps {
  onBackToLogin: () => void
}

const features = [
  {
    icon: <MessageSquare className="w-5 h-5 text-primary-foreground" />,
    title: 'AskREINZ',
    description: 'AI-powered answers to your NZ real estate questions — instantly.',
    preview: previewAskReinz,
    alt: 'AskREINZ chat preview',
  },
  {
    icon: <Home className="w-5 h-5 text-primary-foreground" />,
    title: 'Property Estimate',
    description: 'Search any NZ address and get an instant valuation estimate.',
    preview: previewPropertyTools,
    alt: 'Property Estimate valuation preview',
  },
  {
    icon: <BarChart2 className="w-5 h-5 text-primary-foreground" />,
    title: 'Market Insights',
    description: 'Live NZ market trends, regional data, and sales analytics.',
    preview: previewMarketInsights,
    alt: 'Market Insights preview',
  },
  {
    icon: <Star className="w-5 h-5 text-primary-foreground" />,
    title: 'Offers',
    description: 'Exclusive partner discounts, CPD courses, and 24/7 legal helpline.',
    preview: previewMemberBenefits,
    alt: 'Offers preview',
  },
]

export function LandingScreen({ onBackToLogin }: LandingScreenProps) {
  return (
    <div className="min-h-screen bg-gradient-hero flex flex-col">
      {/* Header */}
      <div className="flex flex-col items-center justify-center px-6 pt-16 pb-8">
        <div className="w-60 h-20 bg-black flex items-center justify-center mb-6 shadow-lg">
          <img src={reinzLogo} alt="REINZ" />
        </div>
        <h1 className="text-2xl font-bold text-primary-foreground mb-2 text-center">
          Built for REINZ Members
        </h1>
        <p className="text-primary-foreground/70 text-sm text-center max-w-xs">
          Tools trusted by New Zealand's licensed real estate professionals.
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 bg-background rounded-t-3xl px-6 pt-8 pb-12 safe-bottom">
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold text-foreground">What's inside?</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Here's a preview of what REINZ members get access to.
          </p>
        </div>

        {/* Feature Cards */}
        <div className="space-y-6 max-w-md mx-auto">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden"
            >
              {/* Card Header */}
              <div className="flex items-center gap-3 px-4 pt-4 pb-3">
                <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center shrink-0">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">{feature.title}</h3>
                  <p className="text-xs text-muted-foreground">{feature.description}</p>
                </div>
              </div>

              {/* Screenshot */}
              <div className="mx-4 mb-4 rounded-xl overflow-hidden border border-border shadow-inner">
                <img
                  src={feature.preview}
                  alt={feature.alt}
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-10 max-w-md mx-auto text-center">
          <p className="text-sm text-muted-foreground mb-4">Already a REINZ member?</p>
          <button
            type="button"
            onClick={onBackToLogin}
            className="w-full flex items-center justify-center gap-2 bg-primary text-white font-medium py-3 rounded-lg hover:bg-primary/90 transition"
          >
            Login
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center text-xs text-muted-foreground">
            <span>Powered by </span>
            <img src={reinzLogoFooter} alt="REINZ" className="w-auto h-6 ml-1" />
          </div>
        </div>
      </div>
    </div>
  )
}
